/*
 * Cart Vehicle Selection and Display Script
 *
 * This script runs on the cart page to ensure that a customer selects
 * a vehicle (year/make/model) before proceeding to checkout. It reads
 * the selection from localStorage (used by the homepage vehicle picker),
 * updates a hidden cart attribute so that the vehicle appears on the
 * order for sales associates, and inserts a “Vehicle:” line beneath
 * each cart item showing the selected year/make/model. If no vehicle
 * has been selected, the script displays a set of dropdowns to allow
 * selection and prevents checkout until a valid vehicle is chosen.
 */

document.addEventListener('DOMContentLoaded', function () {
  const container = document.getElementById('vehicle-selection-container');
  const message = document.getElementById('vehicle-required-message');
  const yearSelect = document.getElementById('cart-vehicle-year');
  const makeSelect = document.getElementById('cart-vehicle-make');
  const modelSelect = document.getElementById('cart-vehicle-model');
  const selectBtn = document.getElementById('cart-vehicle-select-btn');
  const vehicleInput = document.getElementById('CartVehicleAttribute');

  // Attempt to parse any previously selected vehicle from localStorage
  let selectedVehicle = null;
  try {
    selectedVehicle = JSON.parse(localStorage.getItem('selectedVehicle'));
  } catch (err) {
    selectedVehicle = null;
  }

  /**
   * Set the hidden cart attribute based on the current selection. If
   * selection is invalid the attribute will be cleared.
   */
  function setVehicleAttribute() {
    if (!vehicleInput) return;
    if (
      selectedVehicle &&
      selectedVehicle.year &&
      selectedVehicle.make &&
      selectedVehicle.model
    ) {
      vehicleInput.value =
        String(selectedVehicle.year) +
        ' ' +
        selectedVehicle.make +
        ' ' +
        selectedVehicle.model;
    } else {
      vehicleInput.value = '';
    }
  }

  /**
   * Insert or update the vehicle line for each cart item. The line is
   * appended to the item details and has a class of `cart-vehicle-line`
   * so that duplicates can be removed before inserting a new one.
   */
  function updateVehicleLines() {
    const detailsNodes = document.querySelectorAll('.cart-item__details');
    detailsNodes.forEach((detail) => {
      // Remove any existing vehicle lines to avoid duplicates
      const existing = detail.querySelector('.cart-vehicle-line');
      if (existing) existing.remove();
      // Only insert if we have a valid selection
      if (
        selectedVehicle &&
        selectedVehicle.year &&
        selectedVehicle.make &&
        selectedVehicle.model
      ) {
        const p = document.createElement('p');
        p.className = 'product__sku cart-vehicle-line';
        p.textContent =
          'Vehicle: ' +
          selectedVehicle.year +
          ' ' +
          selectedVehicle.make +
          ' ' +
          selectedVehicle.model;
        detail.appendChild(p);
      }
    });
  }

  /**
   * Populate the year dropdown with all distinct years in the dataset,
   * sorted descending. Clears and resets make and model selects.
   */
  function buildYearOptions() {
    if (!yearSelect || !makeSelect || !modelSelect) return;
    yearSelect.innerHTML = '<option value="">Year</option>';
    makeSelect.innerHTML = '<option value="">Make</option>';
    modelSelect.innerHTML = '<option value="">Model</option>';
    if (!window.duallyVehicleData || !Array.isArray(window.duallyVehicleData)) return;
    const years = [];
    window.duallyVehicleData.forEach((item) => {
      const start = parseInt(item.start, 10);
      const end = parseInt(item.end, 10);
      if (isNaN(start) || isNaN(end)) return;
      for (let y = start; y <= end; y++) {
        if (!years.includes(y)) years.push(y);
      }
    });
    years.sort((a, b) => b - a);
    years.forEach((yr) => {
      const opt = document.createElement('option');
      opt.value = String(yr);
      opt.textContent = String(yr);
      yearSelect.appendChild(opt);
    });
  }

  /**
   * Populate the make dropdown based on the selected year.
   */
  function populateMakes() {
    const yearVal = parseInt(yearSelect.value, 10);
    makeSelect.innerHTML = '<option value="">Make</option>';
    modelSelect.innerHTML = '<option value="">Model</option>';
    if (!yearVal || !window.duallyVehicleData) return;
    const makes = Array.from(
      new Set(
        window.duallyVehicleData
          .filter((item) => {
            const start = parseInt(item.start, 10);
            const end = parseInt(item.end, 10);
            return !isNaN(start) && !isNaN(end) && yearVal >= start && yearVal <= end;
          })
          .map((item) => item.make)
      )
    ).sort();
    makes.forEach((mk) => {
      const opt = document.createElement('option');
      opt.value = mk;
      opt.textContent = mk;
      makeSelect.appendChild(opt);
    });
  }

  /**
   * Populate the model dropdown based on the selected year and make.
   */
  function populateModels() {
    const yearVal = parseInt(yearSelect.value, 10);
    const makeVal = makeSelect.value;
    modelSelect.innerHTML = '<option value="">Model</option>';
    if (!yearVal || !makeVal || !window.duallyVehicleData) return;
    const models = Array.from(
      new Set(
        window.duallyVehicleData
          .filter((item) => {
            const start = parseInt(item.start, 10);
            const end = parseInt(item.end, 10);
            return (
              item.make === makeVal &&
              !isNaN(start) &&
              !isNaN(end) &&
              yearVal >= start &&
              yearVal <= end
            );
          })
          .map((item) => item.model)
      )
    ).sort();
    models.forEach((mdl) => {
      const opt = document.createElement('option');
      opt.value = mdl;
      opt.textContent = mdl;
      modelSelect.appendChild(opt);
    });
  }

  // Event listeners for cascading dropdowns
  //
  // When any of the cart vehicle dropdowns change we need to prevent
  // the event from bubbling up to the parent <cart-items> element.
  // The cart component listens for change events on its children and
  // interprets them as quantity updates. Without stopping propagation,
  // selecting a year/make/model triggers quantity validation in
  // cart.js, which emits an error like "You can only add this item in
  // increments of undefined" because our selects do not have min/max/step
  // attributes. Wrapping the handlers and calling e.stopPropagation()
  // ensures the change events are consumed here and do not reach
  // cart-items.
  if (yearSelect) {
    yearSelect.addEventListener('change', function (e) {
      // Prevent bubbling to cart-items
      e.stopPropagation();
      populateMakes();
    });
  }
  if (makeSelect) {
    makeSelect.addEventListener('change', function (e) {
      e.stopPropagation();
      populateModels();
    });
  }
  // Model select doesn't cascade into another dropdown but still needs
  // to stop propagation to avoid quantity validation being triggered.
  if (modelSelect) {
    modelSelect.addEventListener('change', function (e) {
      e.stopPropagation();
    });
  }

  // Handle selection of vehicle via the Select button
  if (selectBtn) {
    selectBtn.addEventListener('click', function () {
      const yearVal = parseInt(yearSelect.value, 10);
      const makeVal = makeSelect.value;
      const modelVal = modelSelect.value;
      // Validate that all three selections are made
      if (!yearVal || !makeVal || !modelVal) {
        if (message) {
          // Trigger flash animation by restarting the class
          message.classList.remove('flash');
          void message.offsetWidth;
          message.classList.add('flash');
        }
        // Scroll the vehicle selection container into view so the
        // customer sees the flashing message, especially on mobile. Use
        // smooth behavior when supported and center the element in the
        // viewport.
        if (container && typeof container.scrollIntoView === 'function') {
          try {
            container.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (err) {
            // Fallback for browsers that do not support options
            container.scrollIntoView();
          }
        }
        return;
      }
      // Save selection to localStorage
      const newVehicle = { year: yearVal, make: makeVal, model: modelVal };
      try {
        localStorage.setItem('selectedVehicle', JSON.stringify(newVehicle));
      } catch (err) {
        console.error('Failed to store selected vehicle', err);
      }
      selectedVehicle = newVehicle;
      // Hide the selection UI and update the hidden attribute and cart display
      if (container) container.style.display = 'none';
      setVehicleAttribute();
      updateVehicleLines();

      /*
       * After saving the vehicle selection we need to ensure that all
       * page-level UI that depends on the stored vehicle is refreshed.
       * The global selected vehicle bar (inserted by
       * selected-vehicle-bar.js) only runs once on page load, so
       * selecting a vehicle from within the cart would normally not
       * display the bar until the customer manually refreshes the page.
       * Likewise, other scripts may read the vehicle selection on
       * load to enable the checkout button. Trigger a full page
       * reload here so that the selected vehicle bar appears
       * immediately and all behaviours relying on the vehicle data
       * initialise correctly. The selection is persisted via
       * localStorage so nothing is lost during the reload.
       */
      try {
        window.location.reload();
      } catch (err) {
        // Fallback for browsers where reload may throw
        window.location.href = window.location.href;
      }
    });
  }

  // Intercept cart checkout buttons to enforce vehicle selection and
  // reliably advance to Shopify checkout. Some browsers/theme setups post
  // the cart form back to /cart instead of carrying the checkout intent
  // through. To avoid that, save the vehicle attribute first and then
  // redirect directly to /checkout.
  const checkoutButtons = document.querySelectorAll('#main-cart-footer button[name="checkout"], button[name="checkout"][form="cart"]');
  if (checkoutButtons.length) {
    const handleCheckout = function (e) {
      // If there is no valid selected vehicle, prevent checkout and flash
      if (
        !selectedVehicle ||
        !selectedVehicle.year ||
        !selectedVehicle.make ||
        !selectedVehicle.model
      ) {
        e.preventDefault();
        if (container) container.style.display = 'block';
        if (message) {
          message.classList.remove('flash');
          void message.offsetWidth;
          message.classList.add('flash');
        }
        // Scroll the vehicle selection container into view when checkout is
        // blocked. This ensures the flashing message is visible even if
        // the checkout button is far down the page on mobile.
        if (container && typeof container.scrollIntoView === 'function') {
          try {
            container.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (err) {
            container.scrollIntoView();
          }
        }
        return false;
      }

      e.preventDefault();
      e.stopPropagation();
      setVehicleAttribute();

      const cartForm = document.getElementById('cart');
      const vehicleValue = vehicleInput ? vehicleInput.value : '';
      const checkoutUrl = '/checkout';

      // Save the cart vehicle attribute before redirecting. This keeps the
      // existing cart/order metadata behaviour intact while bypassing the
      // unreliable cart-form checkout submission path.
      fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            Vehicle: vehicleValue,
          },
        }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
          return response.json();
        })
        .then(() => {
          window.location.assign(checkoutUrl);
        })
        .catch((error) => {
          console.error('Checkout redirect fallback triggered', error);

          // Last-resort fallback: ensure the vehicle value is still present
          // on the cart form and then submit the cart form with an explicit
          // checkout field.
          if (cartForm) {
            let checkoutInput = cartForm.querySelector('input[name="checkout"]');
            if (!checkoutInput) {
              checkoutInput = document.createElement('input');
              checkoutInput.type = 'hidden';
              checkoutInput.name = 'checkout';
              checkoutInput.value = 'Checkout';
              cartForm.appendChild(checkoutInput);
            }
          }

          window.location.assign(checkoutUrl);
        });
    };

    checkoutButtons.forEach((button) => {
      button.addEventListener('click', handleCheckout);
    });
  }

  // Initialisation on page load
  if (
    selectedVehicle &&
    selectedVehicle.year &&
    selectedVehicle.make &&
    selectedVehicle.model
  ) {
    // Hide selection UI when a vehicle is already selected
    if (container) container.style.display = 'none';
    setVehicleAttribute();
    updateVehicleLines();
  } else {
    // Show selection UI and build dropdown options if no vehicle selected
    if (container) container.style.display = 'block';
    if (message) message.style.display = 'block';
    buildYearOptions();
  }
});